import { useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";

import type { ForgeBackgroundHandle } from "../brand/forge-bg";

/**
 * The branded backdrop for every page a signed-out person can reach.
 *
 * One wrapper rather than a canvas per page, deliberately. `/login` and
 * `/set-password` are the two public routes, and a newly provisioned admin
 * meets /set-password FIRST — before they have ever seen the login page. Two
 * separate mounts would restart the scene, and its logo burn-in plays once per
 * start, so following a set-password link and then signing in would play the
 * whole eight-second animation twice.
 *
 * It must not wrap the authenticated routes. A WebGL factory scene running
 * behind the services pages for the length of a session is a battery and
 * memory cost paid for something nobody is looking at.
 */
export function PublicShell() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const handleRef = useRef<ForgeBackgroundHandle | null>(null);

  useEffect(() => {
    // StrictMode mounts effects twice in development. Without this flag the
    // second mount starts a second renderer and the first one's GL context is
    // leaked — the handle from the cancelled start would never be disposed,
    // because the cleanup that runs has already captured a null ref.
    let cancelled = false;

    // Dynamic, so three and its postprocessing addons (~0.6 MB) stay out of
    // the main bundle: they are needed by two public pages, not by the app.
    // The catch is the whole point — a machine with WebGL disabled, a driver
    // blocklist, or a failed chunk fetch must leave a plain, working sign-in
    // page rather than a blank one.
    import("../brand/forge-bg")
      .then(({ startForgeBackground }) => {
        if (cancelled || !canvasRef.current) return;
        // fit:'element' so styles.css can frame the scene 16:10 on portrait
        // phones and Fold-shaped screens instead of cropping the wordmark.
        handleRef.current = startForgeBackground(canvasRef.current, {
          fit: "element",
        });
      })
      .catch(() => {
        /* No background. The page below is unaffected. */
      });

    return () => {
      cancelled = true;
      handleRef.current?.dispose();
      handleRef.current = null;
    };
  }, []);

  return (
    <>
      {/* aria-hidden: it is decoration, and a screen reader announcing a
       * canvas before the sign-in form would be noise. */}
      <canvas ref={canvasRef} className="brand-bg" aria-hidden="true" />
      <div className="public-scope">
        <Outlet />
      </div>
    </>
  );
}
